/* eslint-disable no-console */
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterOutlet, RoutesRecognized } from '@angular/router';
import { filter, pairwise, startWith, tap } from 'rxjs/operators';
import { UrlService } from './common/services/url.service';
import { TranslateService } from '@ngx-translate/core';
import { of, Subscription } from 'rxjs';
import { TranslateKey } from './features/translations/enums/translate-key.enum';
import { PrimeNG } from 'primeng/config';
import { ProgressSpinner } from 'primeng/progressspinner';
import {
  Applications,
  StateStoreKeys,
  StatesService,
  WorkspaceService,
} from '@socium/sociumflow';
import { StorageData } from './common/models/storageData';

/**
 * Serve as the root component for the Angular application.
 */
@Component({
  selector: 'contract-v2-root',
  imports: [RouterOutlet, ProgressSpinner],
  template: `
    @if (isLoading) {
      <div
        class="flex justify-content-center align-items-center"
        style="height: 100vh"
      >
        <p-progressSpinner />
      </div>
    } @else {
      <router-outlet />
    }
  `,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly primengConfig = inject(PrimeNG);
  private readonly workspaceService = inject(WorkspaceService);
  private readonly statesService = inject(StatesService);
  private readonly router = inject(Router);
  private readonly urlService = inject(UrlService);
  private readonly translateService = inject(TranslateService);
  private readonly subscription = new Subscription();
  private authDataSubscription: Subscription | null = null;
  public isLoading = true;

  constructor() {
    this.initTranslation();
  }

  ngOnInit(): void {
    this.primengConfig.ripple.set(true);
    const authData = this.statesService.getCookie<StorageData>(
      StateStoreKeys.TOKEN_NAME,
    );

    this.authDataSubscription = of(authData).subscribe(
      (data: StorageData | null) => {
        if (data?._accessToken && data?._tokenExpirationDate) {
          this.workspaceService
            .setStatesStoreAfterReload(Applications.DOC)
            .then(() => {
              this.workspaceService.initCrossAppLogoutListener();
              this.workspaceService.processIntrospectToken();
              this.isLoading = false;
            })
            .catch((error) => {
              console.error('Error reloading state:', error);
              this.isLoading = false;
            });
          return;
        }

        this.isLoading = false;
      },
    );
    this.initTrackingUrl();
  }

  ngOnDestroy(): void {
    this.authDataSubscription?.unsubscribe();
    this.subscription.unsubscribe();
  }

  public initTranslation(): void {
    this.translatePrimeNg();
    this.listenChangeLanguage();
  }

  private initTrackingUrl(): void {
    const trackingSubscription = this.router.events
      .pipe(
        filter(
          (evt): evt is RoutesRecognized => evt instanceof RoutesRecognized,
        ),
        tap((x) => console.log('Event:', x)),
        startWith({} as RoutesRecognized),
        pairwise(),
        tap((x) => console.log('Pairwise:', x)),
      )
      .subscribe(([previous]: [RoutesRecognized, RoutesRecognized]) => {
        this.urlService.setPreviousUrl(previous.urlAfterRedirects);
      });

    this.subscription.add(trackingSubscription);
  }

  private translatePage(): void {
    this.translatePrimeNg();
  }

  private translatePrimeNg(): void {
    const primeNgTranslation = this.translateService.instant(
      TranslateKey.PRIME_NG_TRANSLATE,
    );
    this.primengConfig.setTranslation(primeNgTranslation);
  }

  private listenChangeLanguage(): void {
    const translationSubscription =
      this.translateService.onLangChange.subscribe(() => {
        this.translatePage();
      });
    this.subscription.add(translationSubscription);
  }
}
